import {
  User,
  Users2,
  Newspaper,
  GraduationCap,
  Clock,
  MessageSquare,
  AlertTriangle,
} from 'lucide-react';

interface ReportCardProps {
  report: {
    id: number;
    type: 'USER' | 'SQUAD' | 'ARTICLE' | 'PROVIDER';
    status: string;
    reporterName: string;
    targetName: string;
    targetType: string;
    reasonCode: string;
    reasonText?: string;
    contextType?: string;
    createdAt: string;
  };
  onClick: () => void;
  isSelected?: boolean;
}

export function ReportCard({ report, onClick, isSelected }: ReportCardProps) {
  // Get icon and color based on report type
  const getTypeConfig = () => {
    switch (report.type) {
      case 'USER':
        return { icon: User, color: 'text-blue-400', bg: 'bg-blue-500/10' };
      case 'SQUAD':
        return { icon: Users2, color: 'text-purple-400', bg: 'bg-purple-500/10' };
      case 'ARTICLE':
        return { icon: Newspaper, color: 'text-green-400', bg: 'bg-green-500/10' };
      case 'PROVIDER':
        return { icon: GraduationCap, color: 'text-amber-400', bg: 'bg-amber-500/10' };
      default:
        return { icon: AlertTriangle, color: 'text-slate-400', bg: 'bg-slate-500/10' };
    }
  };

  const { icon: TypeIcon, color, bg } = getTypeConfig();

  // Get status badge color
  const getStatusColor = () => {
    switch (report.status) {
      case 'PENDING':
        return 'bg-amber-500/20 text-amber-400';
      case 'REVIEWED':
        return 'bg-blue-500/20 text-blue-400';
      case 'RESOLVED':
        return 'bg-green-500/20 text-green-400';
      case 'DISMISSED':
        return 'bg-slate-500/20 text-slate-400';
      default:
        return 'bg-slate-500/20 text-slate-400';
    }
  };

  // Format time ago
  const formatTimeAgo = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) return `${diffDays}d ago`;
    if (diffHours > 0) return `${diffHours}h ago`;
    if (diffMins > 0) return `${diffMins}m ago`;
    return 'Just now';
  };

  return (
    <div
      onClick={onClick}
      className={`p-4 border rounded-lg cursor-pointer transition-all ${
        isSelected
          ? 'border-cyan-500 bg-cyan-500/10'
          : 'border-slate-700 bg-slate-800/50 hover:border-slate-600 hover:bg-slate-800'
      }`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`p-1.5 rounded ${bg}`}>
            <TypeIcon className={`h-4 w-4 ${color}`} />
          </div>
          <div>
            <span className="text-sm font-medium text-white">{report.targetName}</span>
            <span className="text-xs text-slate-500 ml-2">({report.targetType})</span>
          </div>
        </div>
        <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusColor()}`}>
          {report.status}
        </span>
      </div>

      {/* Reason */}
      <div className="mb-3">
        <span className="text-xs font-medium text-slate-400">{report.reasonCode}</span>
        {report.reasonText && (
          <p className="text-sm text-slate-300 mt-1 line-clamp-2">{report.reasonText}</p>
        )}
      </div>

      {/* Context badge */}
      {report.contextType && (
        <div className="mb-3">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-700 rounded text-xs text-slate-300">
            <MessageSquare className="h-3 w-3" />
            {report.contextType} Report
          </span>
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between text-xs text-slate-500">
        <span>Reported by {report.reporterName}</span>
        <span className="flex items-center gap-1">
          <Clock className="h-3 w-3" />
          {formatTimeAgo(report.createdAt)}
        </span>
      </div>
    </div>
  );
}
